/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { currency } from '@utils';
import { useDispatch, useSelector } from 'react-redux';
import { ComboBox, FLink, LoadingIndicator, Button } from '@components';
import './styles.css';
import { format, subMonths } from 'date-fns';
import { pt } from 'date-fns/locale';
import { mdiEyeOff, mdiEye } from '@mdi/js';
import { getExtratoContaRequest } from '../../../store/modules/extratoContribuicao/actions';
import { chartOptions } from './utils/chartOptions';
import { EXTRADO_SALDO_CONTAS } from '../../utils/paths';
import { coresPrincipais } from '../../utils/colors';
import Icons from '../Icon';

export const SaldoCard = () => {
  const custom = useSelector((state) => state.customizaSistema);
  const participante = useSelector((state) => state.user);
  const extrato = useSelector((state) => state.extratoContribuicao);
  const mesCompetencia = useSelector((state) => state.user.corteExtrato);
  const {
    grafico_rendimento: showRendimentos,
    extrato_conta_ativo: showExtrato,
    extrato_aposentadoria: showExtratoAposentadoria,
  } = useSelector((state) => state.telasDisponiveis);

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [exibirSaldo, setExibirSaldo] = useState(false);
  const [labels, setLabels] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartInterval, setChartInterval] = useState(12);
  const [infoSaldoRend, setInfoSaldoRend] = useState({
    saldo: currency(0),
    rendimento: 0,
    dataRendimento: format(subMonths(new Date(), 1), 'MMMM', { locale: pt }),
  });

  const [filtroGrafico, setFiltroGrafico] = useState([]);

  useEffect(() => {
    const filtroDadosRend = extrato.Rentabilidade.filter(
      (rent, index) =>
        rent.DataCompetencia &&
        index >= extrato.Rentabilidade.length - chartInterval
    );
    setFiltroGrafico(filtroDadosRend);
  }, [extrato.Rentabilidade, chartInterval]);

  useEffect(() => {
    if (extrato.done && participante.matricula !== undefined) {
      const rendimentos = extrato.Rentabilidade;

      setInfoSaldoRend({
        saldo: currency(extrato.Saldo),
        rendimento: rendimentos[rendimentos.length - 1].Percentual,
        dataRendimento: format(subMonths(new Date(), 1), 'MMMM', {
          locale: pt,
        }),
      });
    }
  }, [
    infoSaldoRend.saldo,
    extrato.Rentabilidade,
    extrato.done,
    extrato.Saldo,
    participante.status,
    participante.matricula,
  ]);

  useEffect(() => {
    const getSaldoConta = async () => {
      if (!extrato.done && participante.matricula !== undefined) {
        dispatch(getExtratoContaRequest());
      }
      return extrato.Saldo;
    };

    getSaldoConta();
  }, [
    dispatch,
    extrato.Rentabilidade,
    extrato.Saldo,
    extrato.done,
    participante,
  ]);

  useEffect(() => {
    if (extrato.done) setLoading(false);
    else setLoading(true);
  }, [extrato.done]);

  useEffect(() => {
    const changeTimeStamp = (option) => {
      const oneYear = 12;
      const twoYears = 24;
      const dividerParity = Number(mesCompetencia[0]) % 2;
      setLabels(
        extrato.Rentabilidade.filter((rent, index) => {
          if (!rent.DataCompetencia) {
            return false;
          }
          if (
            option === oneYear &&
            index % 2 === dividerParity &&
            index >= extrato.Rentabilidade.length - option
          )
            return true;
          if (
            option === twoYears &&
            index % 4 === 0 &&
            index >= extrato.Rentabilidade.length - option
          )
            return true;
          if (option < oneYear)
            return index >= extrato.Rentabilidade.length - option;
          return false;
        }).map((rent) => rent && rent.DataCompetencia)
      );
      let valorAcumulado = 0;

      setChartSeries([
        {
          name: `Rendimento${option}`,
          data: filtroGrafico.map((item, index) => {
            if (index === 0) {
              valorAcumulado = 1 + 1 * (parseFloat(item.Percentual) / 100);
              return valorAcumulado;
            }

            valorAcumulado +=
              (valorAcumulado * parseFloat(item.Percentual)) / 100;
            return valorAcumulado;
          }),
          originalData: extrato.Rentabilidade.map((item) => item.Percentual),
        },
      ]);

      setChartInterval(option);
    };

    changeTimeStamp(chartInterval);
  }, [chartInterval, extrato.Rentabilidade, filtroGrafico]);

  return !loading ? (
    <div className="card h-100">
      <div className="card-body">
        <div className="row">
          <div className="col-12 col-lg-6 col-xl-4 col-xxl-4 mb-0 mb-md-32 mb-lg-0 d-lg-flex align-items-center">
            <div>
              <div className="mb-5 ">
                {participante?.perfil?.Label && (
                  <>
                    <span className="text-orange fw-bold ts-10">
                      {`PERFIL: ${participante.perfil.Label}`}
                    </span>
                    <br />
                  </>
                )}
                <span className="text-muted fw-bold ts-10">
                  {participante.status}
                </span>
              </div>
              <div className="row">
                <div className="col-10">
                  <p className="ts-16 mb-2">Saldo em conta</p>
                </div>
                <div className="col-2">
                  <Button
                    noBorder
                    className="col-12"
                    onClick={() => {
                      setExibirSaldo(!exibirSaldo);
                    }}
                  >
                    <Icons
                      path={exibirSaldo ? mdiEye : mdiEyeOff}
                      color={coresPrincipais.geral}
                      size={1}
                    />
                  </Button>
                </div>
              </div>

              <div className="row align-items-center">
                {exibirSaldo ? (
                  <p
                    className="ts-40 text-truncate col-12"
                    title={infoSaldoRend.saldo}
                  >
                    <strong>{infoSaldoRend.saldo}</strong>
                  </p>
                ) : (
                  <p className="ts-40 text-truncate text-opacity col-12">
                    <strong>{infoSaldoRend.saldo}</strong>
                  </p>
                )}
              </div>

              <hr className="mb-3 mb-lg-32 d-none d-md-block" />
              {(showExtrato || showExtratoAposentadoria) && (
                <>
                  <FLink to={EXTRADO_SALDO_CONTAS} icon>
                    Veja seu extrato
                  </FLink>
                  <hr className="mt-3 d-block d-md-none" />
                </>
              )}
            </div>
          </div>
          {showRendimentos && (
            <>
              <div className="col-12 col-md-6 col-lg-6 col-xl-3 col-xxl-4 d-flex align-items-center justify-content-center">
                <div className="my-32 my-lg-0">
                  <div className="gradient_wrapper">
                    <div className="rendimento_circle">
                      <div className="text-center">
                        <p className="ts-12 mb-2 fw-bold">
                          {`RENDIMENTO DO PERFIL `}
                        </p>
                        <p className="ts-24 mb-1 fw-bold">
                          {extrato.ultimaRentabilidade.Percentual}%
                        </p>
                        <p>em {extrato.ultimaRentabilidade.MesVigente}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-5 col-xxl-4 mx-auto">
                <div>
                  <ComboBox
                    color={custom.nomeCorPrincipal}
                    className="max-180"
                    label="RENDIMENTO"
                    labelClass="fw-bold ts-12 text-muted mb-2"
                    options={[
                      { value: 3, label: 'Últimos 3 meses' },
                      { value: 5, label: 'Últimos 5 meses' },
                      { value: 6, label: 'Últimos 6 meses' },
                      { value: 12, label: '1 ano' },
                    ]}
                    value={chartInterval}
                    defaultValue={chartInterval}
                    onChange={setChartInterval}
                    tooltip="A curva do gráfico representa a rentabilidade acumulada e os pontos a do mês"
                  />
                  <div className="position-relative">
                    <Chart
                      options={chartOptions(chartSeries)}
                      series={chartSeries}
                      height={200}
                      type="line"
                    />
                    {labels[0] !== undefined && (
                      <div className="chart-labels">
                        {labels.map((label) => {
                          if (label) {
                            return (
                              <p
                                key={`${label}s`}
                                className="tstendi-12 fw-bold text-muted"
                              >
                                {label}
                              </p>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <LoadingIndicator />
  );
};
